import { Box, CircularProgress, useTheme } from "@mui/material";
import UserProfile from "ui/appointment/components/common/user/UserProfile/UserProfile";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import {
  selectCreateAppointment,
  selectCreateTcuAppointment,
} from "ui/appointment/ducks/selectors";
import VerticalSpreadLayout from "lib/components/layout/VerticalSpreadLayout/VerticalSpreadLayout";
import ButtonsFooter from "lib/components/buttons/ButtonsFooter/ButtonsFooter";
import { useNavigate } from "react-router";
import { selectUser } from "lib/redux/user/selectors";
import { useCallback, useMemo, useState } from "react";
import { createTcuAppointments } from "api/appointment/CreateTcuAppointment/createTcuAppointment";
import Page from "lib/components/layout/Page/Page";
import ConfirmationModal from "lib/components/modals/ConfirmationModal/ConfirmationModal";
import { PATHS } from "lib/routing";
import { ErrorModalTitles } from "lib/components/navigation/AppRouteRenderer/ErrorModal/HelperFunctions";
import MapRawStringToReactElement from "lib/util/ReactComponentUtil/mapRawStringToReactElement/MapRawStringtoReactElement";
import { AxiosError } from "axios";
import {
  setTcuSelectedRegInstitutionCode,
  updateTcuRemainingRegInstitutionCodes,
} from "ui/appointment/ducks/appointmentsSlice";
import RescheduleConfirmationCard from "./RescheduleConfirmationCard/RescheduleConfirmationCard";
import { TcuSlotDetails } from "api/appointment/GetTcuAvailableSlots/getTcuAvailableSlots.toUi.types";
import { sxStyles } from "./TcuRescheduleConfirmationPage.styles";
import { logEventToGoogleAnalytics } from "lib/util/GoogleAnalyticsUtil/logEvent";
import { EVENTS } from "lib/util/GoogleAnalyticsUtil/events";
import { navigateToExternalInMobileBrowser } from "lib/routing/navigate/navigate";

const TcuRescheduleConfirmationPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = sxStyles(theme);

  // Redux States
  const {
    bookedSlot,
    rescheduleSlot,
    remainingRegInstitutionCode,
    selectedRegInstitutionCode,
    feedbackUrl,
  } = useAppSelector(selectCreateTcuAppointment);
  const { selectedInstitution, selectedInstitutionCode } = useAppSelector(
    selectCreateAppointment,
  );
  const { memberIdentifier } = useAppSelector(selectUser);

  // Local States
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isErrored, setIsErrored] = useState(false);
  const [errorMessageDisplay, setErrorMessageDisplay] = useState<string | null>(
    null,
  );

  // Transformations
  const newArray = useMemo(
    () =>
      bookedSlot?.slots.map((slotDetails, index) => ({
        old: slotDetails,
        new: rescheduleSlot?.slots[index],
      })),
    [bookedSlot?.slots, rescheduleSlot?.slots],
  );

  // Callbacks
  const sendTcuRescheduleRequest = useCallback(async () => {
    try {
      setIsLoading(true);
      await createTcuAppointments({
        MemberIdentifier: memberIdentifier ?? "",
        OldSlotIdList: bookedSlot?.slots.map((slot) => slot.id) ?? [],
        NewSlotIdList: rescheduleSlot?.slots.map((slot) => slot.id) ?? [],
        InstitutionCode: selectedInstitutionCode ?? "",
        ApptStartTime: rescheduleSlot?.slots[0].date ?? "",
      });
      setIsModalOpen(true);
    } catch (error) {
      setIsErrored(true);
      logEventToGoogleAnalytics(EVENTS.VIEW_TCU_ERROR_PROMPT);
      if (error instanceof AxiosError) {
        setErrorMessageDisplay(error.response?.data.Message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    bookedSlot?.slots,
    memberIdentifier,
    rescheduleSlot?.slots,
    selectedInstitutionCode,
  ]);

  // Handlers
  const handleOnClickConfirm = () => {
    logEventToGoogleAnalytics(EVENTS.CLICK_TCU_APPT_RESC_CONFIRM);
    sendTcuRescheduleRequest();
  };
  const handleOnClickBack = () => {
    navigate(-1);
  };

  // JSX
  return (
    <Page>
      {isLoading ? (
        <Box sx={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : (
        <VerticalSpreadLayout>
          {/* Top section */}
          <Box>
            <Box sx={classes.headerContainer}>
              <UserProfile showMakeAppointmentButton={false} />
            </Box>

            {/* Cards */}
            {newArray?.map((slotDetails) => (
              <RescheduleConfirmationCard
                institutionName={selectedInstitution}
                slotDetails={{
                  old: slotDetails.old,
                  new: slotDetails.new || ({} as TcuSlotDetails),
                }}
              />
            ))}
          </Box>

          {/* Bottom section */}
          <Box sx={classes.footerContainer}>
            <ButtonsFooter
              nextButtonText="Confirm this appointment"
              cancelButtonText="Back"
              onClickNext={handleOnClickConfirm}
              onClickCancel={handleOnClickBack}
            />
          </Box>
        </VerticalSpreadLayout>
      )}

      {/* Error */}
      <ConfirmationModal
        title={ErrorModalTitles.oops}
        open={isErrored}
        body={MapRawStringToReactElement(errorMessageDisplay || "")}
        isNextButtonDisabled={!feedbackUrl}
        nextButtonText={"Make offline request"}
        onClickNext={() => {
          logEventToGoogleAnalytics(EVENTS.CLICK_TCU_ERROR_PROMPT_OFFLINEREQ);
          if (feedbackUrl) {
            navigateToExternalInMobileBrowser(feedbackUrl);
          }
        }}
        cancelButtonText="Go to Appointment"
        onClickCancel={() => {
          navigate(PATHS.APPOINTMENT_MOBILE.path, { replace: true });
        }}
      />

      {/* Success */}
      {remainingRegInstitutionCode &&
      remainingRegInstitutionCode?.length > 1 ? (
        // Prompt user to book tcu appointment for the other domain in the same institution
        <ConfirmationModal
          open={isModalOpen}
          title="You Have Another Appointment to Confirm"
          hideNextButton={false}
          nextButtonText="Proceed to confirm"
          cancelButtonText="Back to Appointment"
          onClickNext={() => {
            logEventToGoogleAnalytics(
              EVENTS.CLICK_TCU_APPT_RESC_PROMPT_PROCEEDTOCFM,
            );
            const nextRegInstitutionCode = remainingRegInstitutionCode.find(
              (x) => x !== selectedRegInstitutionCode,
            );

            dispatch(
              setTcuSelectedRegInstitutionCode(nextRegInstitutionCode || ""),
            );
            dispatch(
              updateTcuRemainingRegInstitutionCodes(
                remainingRegInstitutionCode.filter(
                  (item) => item !== selectedRegInstitutionCode,
                ),
              ),
            );
            // repeat the process
            setIsModalOpen(false);
            navigate(PATHS.APPOINTMENT_CREATE_TCU_CONFIRM.path, {
              replace: true,
            });
          }}
          onClickCancel={() => {
            logEventToGoogleAnalytics(
              EVENTS.CLICK_TCU_APPT_RESC_PROMPT_BACKTOAPPT,
            );
            navigate(PATHS.APPOINTMENT_MOBILE.path, { replace: true });
          }}
        />
      ) : (
        <ConfirmationModal
          open={isModalOpen}
          title="Appointment Booked"
          hideCancelButton={true}
          nextButtonText="Go to Appointment"
          onClickNext={() => {
            navigate(PATHS.APPOINTMENT_MOBILE.path, {
              replace: true,
            });
          }}
        />
      )}
    </Page>
  );
};

export default TcuRescheduleConfirmationPage;
